/* ── BRIEF 124 §4 — THE FIRM'S OWN ADDRESS, AND NEVER SOMEBODY ELSE'S ────────
 *
 * [MEASURED] `mtgDefLoc` fell back to a HARDCODED
 *
 *     "WATT Legal - Avenue Louise / Louizalaan 64, Bruxelles/Brussel 1050"
 *
 * (src/App.tsx, until this brief). It is the third instance of the same disease
 * this slice has now removed three times: one firm's data written into the
 * product. A second firm — or the art-gallery vertical — inherited WATT's
 * street as its default meeting location, on every new event, silently.
 *
 * THE FALLBACK IS EMPTY, NOT A GUESS. `""` renders an empty location field the
 * user fills; a wrong address renders a confident one nobody checks, and it
 * travels into the invitation. An honest blank beats a plausible lie —
 * `firstMeetingTypeCode`'s `null` rule (utils/taxonomyMap), applied to an
 * address.
 *
 * The shape is `FirmSettings.firm`, the same object Settings edits
 * (SettingsView `updFirm`) and the pro-forma header renders: `{ name, street,
 * postalCode, city, country }`. Every field optional, because a fresh tenant
 * has filled none of them — which is exactly the case that must produce `""`
 * rather than a string of separators.
 *
 * `country` IS DELIBERATELY OMITTED. This is a meeting location for people who
 * are already in the country, not a postal address; the pro-forma header is the
 * surface that needs the full one, and it composes its own.
 *
 * 207 §12 [RULED] — THE STREET AND THE CITY IN THE READER'S LANGUAGE: `lang`
 * picks a filled variant (src/lib/firmAddress.mjs, « Louizalaan » for a Dutch
 * reader where « Avenue Louise » is typed); absent, the address as typed.
 */

import { firmAddressIn } from "./firmAddress.mjs"; // 207 §12 — the street and the city in a language

export type FirmIdentity = {
  name?: string | null;
  street?: string | null;
  postalCode?: string | null;
  city?: string | null;
} | null | undefined;

export function firmDefaultLocation(firm: FirmIdentity, lang?: string): string {
  if (!firm) return "";
  const clean = (v: unknown) => String(v ?? "").trim();
  const a = firmAddressIn(firm, lang);
  const name = clean(firm.name);
  const street = clean(a.street);
  const town = [clean(a.postalCode), clean(a.city)].filter(Boolean).join(" ");
  const address = [street, town].filter(Boolean).join(", ");
  /* The separator the hardcoded literal used, kept so a firm that fills its
     settings in gets the string it already recognises. Name alone, or address
     alone, is a legitimate answer — a firm that has typed only its name gets
     its name, not its name and a dangling dash. */
  return [name, address].filter(Boolean).join(" - ");
}
